/**
 * SharedUI.js — Componentes UI compartidos, theme-aware + i18n.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

// ── ConfirmModal ──────────────────────────────────────────────────────────────
export function ConfirmModal({ open, name, message, onConfirm, onCancel, loading }) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
      backdropFilter:"blur(3px)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center",
    }} onClick={onCancel}>
      <div style={{
        background:"var(--bg-card)", border:"1px solid var(--border)",
        borderRadius:12, padding:24, zIndex:1001,
        boxShadow:"0 24px 64px var(--shadow)",
        width:"min(400px,90vw)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ textAlign:"center", marginBottom:16 }}>
          <div style={{ fontSize:"2.5rem", marginBottom:8 }}>🗑️</div>
          <h3 style={{ color:"var(--text-primary)", margin:0 }}>
            {t("common.eliminarRegistro")}
          </h3>
          <p style={{ color:"var(--text-secondary)", marginTop:6, fontSize:"0.88rem" }}>
            {message ?? (
              <>
                {t("common.eliminandoPermanente")}{" "}
                <strong style={{ color:"#f87171" }}>{name}</strong>{" "}
                {t("common.permanentemente")}
              </>
            )}
          </p>
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
          <button type="button" onClick={onCancel} style={{
            padding:"8px 20px",
            background:"var(--hover-bg)",
            border:"1px solid var(--border-input)",
            borderRadius:7,
            color:"var(--text-secondary)",
            cursor:"pointer", fontSize:"0.84rem",
          }}>
            {t("common.cancelar")}
          </button>
          <button type="button" onClick={onConfirm} disabled={loading} style={{
            padding:"8px 20px",
            background:"rgba(239,68,68,0.15)",
            border:"1px solid rgba(239,68,68,0.4)",
            borderRadius:7, color:"#f87171",
            cursor:loading ? "not-allowed" : "pointer",
            fontSize:"0.84rem", fontWeight:600,
            opacity:loading ? 0.7 : 1,
          }}>
            {loading
              ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("common.eliminando")}</>
              : t("common.siEliminar")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ActionButtons ─────────────────────────────────────────────────────────────
export function ActionButtons({ onEdit, onDelete }) {
  const { t } = useTranslation();
  return (
    <div className="d-flex align-items-center justify-content-start" style={{ gap:8 }}>
      {onEdit && (
        <button type="button" onClick={onEdit} title={t("common.editar")} style={{
          background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.3)",
          borderRadius:6, padding:"4px 10px", color:"#f97316", cursor:"pointer",
        }}>
          <i className="fa-solid fa-square-pen"/>
        </button>
      )}
      {onDelete && (
        <button type="button" onClick={onDelete} title={t("common.eliminar")} style={{
          background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.25)",
          borderRadius:6, padding:"4px 10px", color:"#f87171", cursor:"pointer",
        }}>
          <i className="fa-solid fa-trash"/>
        </button>
      )}
    </div>
  );
}

// ── useToast ──────────────────────────────────────────────────────────────────
export function useToast() {
  const [toast, setToast] = useState(null);

  function showToast(text, ok = true) {
    setToast({ text, ok });
    setTimeout(() => setToast(null), 4000);
  }

  const toastEl = toast ? (
    <div style={{
      position:"fixed", bottom:24, right:24, zIndex:2000,
      background: toast.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
      border:`1px solid ${toast.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
      borderLeft:`4px solid ${toast.ok ? "#4ade80" : "#f87171"}`,
      borderRadius:8, padding:"12px 18px", fontSize:"0.87rem",
      display:"flex", alignItems:"center", gap:10,
      boxShadow:"0 8px 32px var(--shadow)", maxWidth:380,
      backgroundColor:"var(--bg-card)",
    }}>
      <i className={`fa-solid ${toast.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
         style={{ color: toast.ok ? "#4ade80" : "#f87171", fontSize:"1.1rem" }}/>
      <span style={{ color: toast.ok ? "#4ade80" : "#f87171", fontWeight:600 }}>
        {toast.text}
      </span>
    </div>
  ) : null;

  return { showToast, toastEl };
}

// ── ErrorBanner ───────────────────────────────────────────────────────────────
export function ErrorBanner({ msg, onDismiss }) {
  if (!msg) return null;
  return (
    <div style={{
      background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)",
      borderLeft:"4px solid #ef4444", borderRadius:8,
      padding:"10px 14px", marginBottom:12, color:"#f87171",
      fontSize:"0.85rem", display:"flex", alignItems:"center", gap:8,
    }}>
      <i className="fa-solid fa-circle-exclamation"/>
      <span style={{ flex:1 }}>{msg}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background:"none", border:"none", color:"#f87171", cursor:"pointer",
        }}>×</button>
      )}
    </div>
  );
}
